import React, {useMemo} from 'react';
import {useAtom} from 'jotai';
import {type FieldPath, type FieldValues, useFormState} from 'react-hook-form';
import {useAttributeFormActionsProvider} from '@netx/core-attribute/context/AttributeFormActionsContext';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form/ReactHookFormControl';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {i18n} from '@netx/core/i18n';
import {isEmptyValue} from '@netx/core/utils';
import {emptyValueSymbol} from '../../constants';
import {isMultiValue} from '../../utils';
import validators from '../../validators';
import {
	EditableAttribute,
	type EditableAttributeProps,
} from './EditableAttribute';

export interface ReactHookFormEditableAttributeProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		Omit<EditableAttributeProps, 'name'>,
		TFieldValues,
		TName
	> {
	useActions?: boolean;
	fieldName?: TName;
	ignoreMandatory?: boolean;
}

/**
 * `react-hook-form` wrapper for `EditableAttribute`
 */
export const ReactHookFormEditableAttributePrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		useActions,
		fieldName,
		ignoreMandatory,
		ControllerProps,
		...props
	}: ReactHookFormEditableAttributeProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	fieldName = fieldName ?? (`${props.attrData.attributeid}` as TName);

	const {defaultValues} = useFormState({control: props.control});

	const {getActionAtom} = useAttributeFormActionsProvider();

	const originalValue =
		defaultValues?.[fieldName as keyof typeof defaultValues];

	const actionAtom = useMemo(
		() => getActionAtom(fieldName!),
		[fieldName, getActionAtom],
	);
	const [action, setAction] = useAtom(actionAtom);

	const hasMultipleValues = props.multipleValues === true; // multipleValues?.[fieldName] === true;
	// console.log(fieldName, field, fieldState);
	const multiValue = isMultiValue(props.attrData.type);

	const required = useMemo(() => {
		// we are enforcing mandatory if we:
		//  * started with a value
		//  * do not have multiple values
		// if neither of those are true we fallback to the prop passed in
		// console.log("REQUIRE CHECK", props.attrData.name, 'originalValue', originalValue)
		return (
			!ignoreMandatory &&
			props.attrData.mandatory === 1 &&
			(!isEmptyValue(originalValue) && !hasMultipleValues
				? true
				: props.enforceMandatory !== false)
		);
	}, [
		hasMultipleValues,
		ignoreMandatory,
		originalValue,
		props.attrData.mandatory,
		props.enforceMandatory,
		props.multipleValues,
	]);

	return (
		<ReactHookFormControl<TFieldValues, TName>
			disabled={props.disabled}
			{...ControllerProps}
			name={fieldName as TName}
			control={props.control}
			render={({field, fieldState}) => {
				const wasArray = multiValue || Array.isArray(field.value);

				const value =
					field.value != null && field.value !== emptyValueSymbol
						? !multiValue && Array.isArray(field.value)
							? field.value[0] ?? ''
							: field.value
						: multiValue
						? []
						: '';

				return (
					<EditableAttribute
						required={required}
						{...props}
						{...field}
						ref={ref}
						inputRef={combineRefs(props.inputRef, field.ref)}
						action={action}
						error={fieldState.error?.message}
						multipleValues={hasMultipleValues}
						value={value}
						onActionChange={useConstCallback(
							splitEventHandler(
								props.onActionChange,
								useActions ? setAction : undefined,
							),
						)}
						// handle the setting/clearing current action based on value changes
						onValueChange={useConstCallback(
							splitEventHandler(props.onValueChange, (value) => {
								if (value != null && !Array.isArray(value)) value = [value];

								if (useActions) {
									// we were cleared and are no longer due to user input
									if (value != null && action === 'clear') {
										field.onChange(value);
										setAction('overwrite');
										return;
									}

									// we had a value and cleared it via user input
									// our form hook will handle updating the value apprpriately for being "cleared"
									if (
										value == null &&
										!required &&
										originalValue != null &&
										originalValue !== emptyValueSymbol
									) {
										setAction('clear');
										return;
									}
								}

								// in all other cases, just pass along
								let _value = value;
								if (_value === null || _value === void 0) {
									if (useActions) {
										// TODO: all part of the hack to recognize an empty value vs a cleared value
										_value = (
											originalValue === emptyValueSymbol
												? emptyValueSymbol
												: undefined
										) as any;
									} else if (wasArray) {
										_value = [];
									}
								}
								field.onChange(_value);
							}),
						)}
					/>
				);
			}}
			rules={{
				required: required ? i18n.t('i18n.mandatory') : undefined,
				validate: async (value, values) => {
					// TODO: looks like they fully take care of required validation when set above
					// console.log('validate', fieldName, value, values);
					return required && (isEmptyValue(value) || value === emptyValueSymbol)
						? i18n.t('i18n.mandatory')
						: validators[props.attrData.type as keyof typeof validators](
								value,
								props.attrData,
						  );
				},
				...ControllerProps?.rules,
			}}
		/>
	);
};

export const ReactHookFormEditableAttribute = React.forwardRef(
	ReactHookFormEditableAttributePrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormEditableAttributeProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormEditableAttribute;
